import { saveDocument } from '../documentDownload';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from '@/app/providers/ToastProvider';
import { useI18n } from '@/hooks/useI18n';
import { ApiError } from '@/lib/api/client';
import {
  workflowApi,
  workflowTitles,
  pathValue,
  type JsonRecord,
  type Resource,
  type DefenseGroup,
  type WorkflowField,
  type WorkflowModule,
  type WorkflowOverview,
  type WorkflowState,
} from '../workflowApi';
import { Card, Hero } from './dp120Parts';
import { Resources } from './DoctoralResources';
import { DoctoralDocumentPreview } from './DoctoralDocumentPreview';
import type { DoctoralViewKey } from './DoctoralWorkspaceOverlay';

const statuses: Record<string, string> = {
  NOT_STARTED: 'Не начато',
  DRAFT: 'Черновик',
  SUBMITTED: 'На проверке',
  ACCEPTED: 'Подтверждено',
  NEEDS_CHANGES: 'Нужны исправления',
  PARTIALLY_ACCEPTED: 'Частично подтверждено',
  READY: 'Заполнено',
};
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
function setPath(data: JsonRecord, path: string, value: unknown) {
  const parts = path.split('.');
  let cursor: JsonRecord = data;
  parts.forEach((key, i) => {
    if (i === parts.length - 1) cursor[key] = value;
    else {
      if (!cursor[key] || typeof cursor[key] !== 'object')
        cursor[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
      cursor = cursor[key] as JsonRecord;
    }
  });
}
const errorText = (e: unknown) =>
  e instanceof Error ? e.message : 'Не удалось выполнить действие';

export function DoctoralWorkflowWorkspace({
  view,
  onNavigate,
  onOverview,
  studentId,
}: {
  view: DoctoralViewKey | 'prime';
  onNavigate: (view: string) => void;
  onOverview?: (data: WorkflowOverview) => void;
  studentId?: string;
}) {
  const { locale } = useI18n();
  const [overview, setOverview] = useState<WorkflowOverview>();
  const [error, setError] = useState('');
  const [visited, setVisited] = useState<WorkflowModule[]>([]);
  const callback = useRef(onOverview);
  callback.current = onOverview;
  const refresh = useCallback(() => {
    void workflowApi
      .overview(studentId)
      .then((data) => {
        setOverview(data);
        callback.current?.(data);
        setError('');
      })
      .catch((e) => setError(errorText(e)));
  }, [studentId]);
  useEffect(refresh, [refresh, view]);
  useEffect(() => {
    if (view in workflowTitles && view !== 'profile')
      setVisited((old) =>
        old.includes(view as WorkflowModule) ? old : [...old, view as WorkflowModule],
      );
  }, [view]);
  return (
    <>
      {error && (
        <p role="alert">
          {error}{' '}
          <button className="dp120-mini" onClick={refresh}>
            Повторить загрузку
          </button>
        </p>
      )}
      {['overview', 'documents', 'sources', 'guide', 'prime'].includes(view) &&
        !overview &&
        !error && <p role="status">Загрузка досье…</p>}
      {view === 'overview' && overview && (
        <>
          <Hero
            kicker="DOCTORAL"
            title="Обзор докторантуры"
            text={`Готовность обязательных полей: ${overview.percent ?? 0}%. Подтверждение консультанта учитывается отдельно.`}
          />
          <div className="dp120-grid">
            {overview.modules.map((m) => (
              <Card
                key={m.module}
                title={workflowTitles[m.module]}
                sub={`${m.filled} / ${m.required} · ${statuses[m.review_status] ?? m.review_status}`}
              >
                <button className="dp120-mini" onClick={() => onNavigate(m.module)}>
                  Открыть · {m.percent === null ? 'Нет обязательных полей' : `${m.percent}%`}
                </button>
              </Card>
            ))}
          </div>
        </>
      )}
      {['documents', 'sources'].includes(view) && overview && (
        <>
          <Hero
            kicker="RESOURCES"
            title="Образцы и видеоинструкции"
            text="Общие материалы действующего Apps Script. Образец не становится документом вашего досье автоматически."
          />
          <Resources resources={overview.resources} />
        </>
      )}
      {view === 'guide' && (
        <Hero
          kicker="GUIDE"
          title="Как работать с досье"
          text="Заполните применимые поля, загрузите свои документы и отправьте раздел на проверку. Изменение отправленной версии возвращает раздел в черновик. Состояние сохранения показано в каждом разделе."
        />
      )}
      {view === 'prime' && overview && (
        <>
          <Hero
            kicker="PRIME"
            title="Научная статья и публикационная стратегия"
            text="Вводный модуль из Apps Script. Полный авторский курс проводится отдельно."
          />
          {overview.prime.map((p) => (
            <Card key={p.id} title={p.t[locale]} span="span12">
              <p>{p.p[locale]}</p>
            </Card>
          ))}
        </>
      )}
      {visited.map((module) => (
        <div key={module} hidden={view !== module}>
          <DoctoralWorkflowForm
            module={module}
            active={view === module}
            onChanged={refresh}
            studentId={studentId}
            onNavigate={onNavigate}
          />
        </div>
      ))}
    </>
  );
}

export function DoctoralWorkflowForm({
  module,
  onChanged,
  studentId,
  active = true,
  onNavigate,
}: {
  module: WorkflowModule;
  active?: boolean;
  onNavigate?: (view: string) => void;
  onChanged?: () => void;
  studentId?: string;
}) {
  const { locale } = useI18n();
  const [state, setState] = useState<WorkflowState>();
  const [profile, setProfile] = useState<JsonRecord>({});
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveProgress, setSaveProgress] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [autosavePaused, setAutosavePaused] = useState(false);
  const [section, setSection] = useState('all');
  const [mode, setMode] = useState<'instruction' | 'templates' | 'fill'>('instruction');
  const [commentTarget, setCommentTarget] = useState('');
  const [preview, setPreview] = useState<JsonRecord>();
  const [historical, setHistorical] = useState<WorkflowState>();
  const snapshotRequest = useRef(0);
  const [evidenceKind, setEvidenceKind] = useState('other');
  const [publicationId, setPublicationId] = useState('');
  const [entry, setEntry] = useState<JsonRecord>({});
  const entryCurrent = useRef<JsonRecord>({});
  const entryDirty = useRef(false);
  const toast = useContext(ToastContext);
  const teardown = useRef<() => void>(() => undefined);
  const [comment, setComment] = useState('');
  const dirty = useRef<JsonRecord>({});
  const current = useRef<WorkflowState>();
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const alive = useRef(true);
  const persistOnLeave = useRef<(() => void) | null>(null);
  const [editTick, setEditTick] = useState(0);
  const accept = useCallback((data: WorkflowState) => {
    current.current = data;
    if (alive.current) {
      setState(data);
      const merged = clone(data.profile);
      Object.entries(dirty.current).forEach(([path, value]) => setPath(merged, path, value));
      setProfile(merged);
      setPending(Object.keys(dirty.current).length > 0 || entryDirty.current);
    }
  }, []);
  const serialized = useCallback((operation: () => Promise<unknown>) => {
    const next = queue.current.catch(() => undefined).then(operation);
    queue.current = next;
    return next;
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      teardown.current();
      persistOnLeave.current?.();
    };
  }, []);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void serialized(async () => {
      if (cancelled || Object.keys(dirty.current).length || entryDirty.current) return;
      const data = await workflowApi.load(module, studentId);
      if (!cancelled) accept(data);
    }).catch((e) => {
      if (!cancelled) setError(errorText(e));
    });
    return () => {
      cancelled = true;
    };
  }, [module, studentId, active, accept, serialized]);
  const flush = useCallback(
    async (kind: 'manual' | 'autosave' = 'autosave') => {
      if (!current.current || studentId) return;
      if (alive.current) setSaveProgress(true);
      try {
        if (
          (entryDirty.current || (kind === 'manual' && entryCurrent.current.id)) &&
          ['publications', 'achievements'].includes(module)
        ) {
          const captured = clone(entryCurrent.current);
          if (kind === 'autosave' && !String(captured.title || '').trim()) return;
          const result = await workflowApi.record(
            module,
            current.current.revision,
            captured,
            'save',
            kind,
          );
          const saved =
            (result.data as JsonRecord[]).find((r) => r.id === captured.id) ||
            (result.data as JsonRecord[]).at(-1);
          if (JSON.stringify(entryCurrent.current) === JSON.stringify(captured)) {
            entryDirty.current = false;
            entryCurrent.current =
              kind === 'autosave' ? { ...captured, id: saved?.id || captured.id } : {};
            if (alive.current) setEntry(entryCurrent.current);
          } else if (saved && !entryCurrent.current.id) {
            entryCurrent.current = { ...entryCurrent.current, id: saved.id };
            if (alive.current) setEntry(entryCurrent.current);
          }
          accept(result);
          onChanged?.();
          return;
        }
        const values = clone(dirty.current);
        if (!Object.keys(values).length && kind !== 'manual') return;
        if (['publications', 'achievements'].includes(module)) return;
        const result = await workflowApi.save(module, current.current.revision, values, kind);
        Object.entries(values).forEach(([k, v]) => {
          if (JSON.stringify(dirty.current[k]) === JSON.stringify(v)) delete dirty.current[k];
        });
        accept(result);
        onChanged?.();
      } finally {
        if (alive.current) setSaveProgress(false);
      }
    },
    [module, studentId, accept, onChanged],
  );
  teardown.current = () => {
    if (
      !studentId &&
      !conflict &&
      !autosavePaused &&
      (Object.keys(dirty.current).length || entryDirty.current)
    ) {
      void serialized(() => flush()).catch((e: unknown) => {
        toast?.notify(`Не удалось сохранить раздел при выходе: ${errorText(e)}`, 'error');
      });
    }
  };
  const failed = useCallback((e: unknown) => {
    if (alive.current) {
      setError(errorText(e));
      if (e instanceof ApiError && e.status === 409) {
        setConflict(true);
        setAutosavePaused(true);
      }
    }
  }, []);
  persistOnLeave.current = () => {
    if (Object.keys(dirty.current).length || entryDirty.current) {
      void serialized(() => flush()).catch(failed);
    }
  };
  useEffect(() => {
    if (!editTick || conflict || autosavePaused || studentId) return;
    const timer = window.setTimeout(() => {
      void serialized(() => flush())
        .then(() => {
          if (alive.current) setError('');
        })
        .catch(failed);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [editTick, conflict, autosavePaused, studentId, serialized, flush, failed]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (Object.keys(dirty.current).length || entryDirty.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  const edit = (path: string, value: unknown) => {
    dirty.current[path] = value;
    setProfile((old) => {
      const next = clone(old);
      setPath(next, path, value);
      return next;
    });
    setPending(true);
    setEditTick((n) => n + 1);
  };
  const changeEntry = (next: JsonRecord, dirtyValue = true) => {
    entryCurrent.current = next;
    entryDirty.current = dirtyValue;
    setEntry(next);
    setPending(dirtyValue || Object.keys(dirty.current).length > 0);
    if (dirtyValue) setEditTick((n) => n + 1);
  };
  const act = (operation?: (s: WorkflowState) => Promise<WorkflowState>) => {
    setBusy(true);
    void serialized(async () => {
      await flush('manual');
      if (Object.keys(dirty.current).length || entryDirty.current)
        throw new Error('Есть более новые изменения. Сохраните их перед отправкой.');
      if (operation && current.current) accept(await operation(current.current));
      onChanged?.();
    })
      .then(() => {
        setError('');
        setAutosavePaused(false);
      })
      .catch(failed)
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  };
  const openDocument = async (record: JsonRecord) => {
    try {
      const downloaded = await workflowApi.document(
        String(record.fileId),
        String(record.versionId),
        studentId,
        'attachment',
        historical?.snapshot_id,
      );
      saveDocument(downloaded, String(record.name || 'document'));
    } catch (e) {
      failed(e);
    }
  };
  const fieldReview = (f: WorkflowField) => (
    <div className="dp120-row-actions">
      <span>
        {f.review?.approved
          ? 'Подтверждено'
          : f.review?.stale
            ? 'Изменено после проверки'
            : 'Требует проверки'}
      </span>
      {f.review?.comment && <p>{f.review.comment}</p>}
      {studentId && !historical && module !== 'profile' && (
        <>
          <button
            className="dp120-mini"
            disabled={busy || !f.done}
            onClick={() =>
              act((s) =>
                workflowApi.fieldDecision(module, s.revision, studentId, f.path, true, comment),
              )
            }
          >
            Подтвердить поле
          </button>
          <button
            className="dp120-mini"
            disabled={busy}
            onClick={() =>
              act((s) =>
                workflowApi.fieldDecision(module, s.revision, studentId, f.path, false, comment),
              )
            }
          >
            Замечание к полю
          </button>
        </>
      )}
    </div>
  );
  const fileField = (f: WorkflowField, path = f.path) => {
    const value = pathValue(historical?.profile || profile, path);
    const documents = (Array.isArray(value) ? value : value ? [value] : []) as unknown[];
    return (
      <div className="dp120-doc" key={path}>
        <div>
          <h4>
            {state?.templates?.find((t) => t.field_path === path)?.number
              ? `${state.templates.find((t) => t.field_path === path)!.number}. `
              : ''}
            {f.label[locale]}
            {f.required ? ' *' : ''}
          </h4>
          {documents.map((v, i) =>
            typeof v === 'object' && v !== null && 'fileId' in v ? (
              <div className="dp120-row-actions" key={i}>
                <span>{String((v as JsonRecord).name)}</span>
                <button className="dp120-mini" onClick={() => setPreview(v as JsonRecord)}>
                  Открыть
                </button>
                <button className="dp120-mini" onClick={() => void openDocument(v as JsonRecord)}>
                  Скачать
                </button>
                {!studentId && !historical && (
                  <button
                    className="dp120-mini"
                    disabled={busy || conflict}
                    onClick={() =>
                      act((s) =>
                        workflowApi.detach(
                          module,
                          s.revision,
                          path,
                          String((v as JsonRecord).fileId),
                        ),
                      )
                    }
                  >
                    Убрать из досье
                  </button>
                )}
              </div>
            ) : (
              <p key={i} className="dp120-badge warn">
                Старая ссылка: {String(v)}. Загрузите документ для проверки.
              </p>
            ),
          )}
          {documents.map((v, i) =>
            v && typeof v === 'object' && (v as JsonRecord).evidenceKind ? (
              <p key={`kind-${i}`}>
                Вид материала:{' '}
                {state?.evidence_types?.find(
                  ([key]) => key === (v as JsonRecord).evidenceKind,
                )?.[1] || String((v as JsonRecord).evidenceKind)}{' '}
                {(v as JsonRecord).linkedPublicationId ? '· Связано с публикацией' : ''}
              </p>
            ) : null,
          )}
          {!studentId && !historical && path.endsWith('.evidence') && (
            <div className="dp120-form">
              <label className="dp120-field">
                <span>Вид подтверждающего материала</span>
                <select value={evidenceKind} onChange={(e) => setEvidenceKind(e.target.value)}>
                  {state?.evidence_types?.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dp120-field">
                <span>Связать с публикацией</span>
                <select value={publicationId} onChange={(e) => setPublicationId(e.target.value)}>
                  <option value="">Без связи</option>
                  {((profile.publications || []) as JsonRecord[]).map((p) => (
                    <option key={String(p.id)} value={String(p.id)}>
                      {String(p.title)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {fieldReview(f)}
          {!documents.length && <span className="dp120-badge warn">Не загружено</span>}
        </div>
        {!studentId && !historical && (
          <label className="dp120-mini mint">
            Загрузить
            <input
              type="file"
              disabled={busy || conflict}
              className="dp120-hidden-file"
              aria-label={`Загрузить: ${f.label[locale]}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file)
                  act((s) =>
                    path.endsWith('.evidence')
                      ? workflowApi.upload(module, s.revision, path, file, {
                          evidence_kind: evidenceKind,
                          ...(publicationId ? { publication_id: publicationId } : {}),
                        })
                      : workflowApi.upload(module, s.revision, path, file),
                  );
              }}
            />
          </label>
        )}
      </div>
    );
  };
  if (!state) return <p role={error ? 'alert' : 'status'}>{error || 'Загрузка раздела…'}</p>;

  const group = (f: WorkflowField) =>
    module === 'nird'
      ? f.sub !== undefined
        ? `Семестр ${Number(f.sub) + 1}`
        : 'Итоги НИРД'
      : module === 'completion'
        ? {
            program: 'Завершение программы',
            employment: 'Трудоустройство',
            workoff: 'Отработка',
            archive: 'Архив',
            clearance: 'Обходной лист',
            degree: 'Защита и диплом',
          }[f.sub || ''] || 'Общие сведения'
        : 'Основная форма';
  const displayFields = historical?.fields || state.fields;
  const groups = [...new Set(displayFields.map(group))];
  const registry = module === 'publications' || module === 'achievements';
  const displayedProfile = historical?.profile || profile;
  const rows = (
    Array.isArray(displayedProfile[module]) ? displayedProfile[module] : []
  ) as JsonRecord[];
  const displayState = historical || state;
  return (
    <>
      <Hero
        kicker="DOCTORAL"
        title={state.instruction?.title[locale] || workflowTitles[module]}
        text={`${displayState.filled} / ${displayState.required} обязательных полей · ${statuses[displayState.review_status] || displayState.review_status}`}
      />
      <p>
        Подтверждено консультантом: {state.approved ?? 0} / {state.required} обязательных полей.
      </p>
      <p role="status">
        {busy || saveProgress
          ? 'Сохранение…'
          : pending
            ? 'Есть несохранённые изменения'
            : state.last_save_kind === 'autosave'
              ? 'Автосохранение выполнено'
              : state.last_save_kind === 'manual'
                ? 'Сохранено вручную'
                : 'Сохранено'}
      </p>
      {error && <p role="alert">{error}</p>}
      {conflict && (
        <div>
          <p>
            Сохранённая версия изменилась. Ваш ввод сохранён в форме. Загрузите актуальную версию и
            проверьте различия перед повторным сохранением.
          </p>
          <button
            className="dp120-mini"
            onClick={() =>
              void workflowApi
                .load(module, studentId)
                .then((data) => {
                  accept(data);
                  setConflict(false);
                  setError('');
                })
                .catch(failed)
            }
          >
            Обновить версию, сохранив мой ввод
          </button>
        </div>
      )}
      {state.confirmation_cleared && (
        <p className="dp120-badge warn">Данные изменились. Требуется повторное подтверждение.</p>
      )}
      {state.advisor_comment && (
        <Card title="Комментарий консультанта" span="span12">
          <p>
            {state.comment_target
              ? `Замечание к: ${state.fields.find((f) => f.path === state.comment_target)?.label[locale] || state.comment_target}`
              : 'Замечание к разделу'}
          </p>
          <p>{state.advisor_comment}</p>
        </Card>
      )}
      <nav className="dp120-subtabs" role="tablist" aria-label="Режимы раздела">
        {(['instruction', 'templates', 'fill'] as const).map((key, i) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            className={mode === key ? 'active gold' : ''}
            onClick={() => setMode(key)}
          >
            {
              {
                ru: ['Инструкция', 'Образцы', 'Заполнение'],
                kz: ['Нұсқаулық', 'Үлгілер', 'Толтыру'],
                en: ['Instructions', 'Templates', 'Complete the form'],
              }[locale][i]
            }
          </button>
        ))}
      </nav>
      <p>
        Ответы:{' '}
        {displayState.answers_filled ??
          displayState.fields.filter((f) => f.required && f.applies && f.kind !== 'file' && f.done)
            .length}{' '}
        /{' '}
        {displayState.answers_required ??
          displayState.fields.filter((f) => f.required && f.applies && f.kind !== 'file').length}
        {' · '}Документы:{' '}
        {displayState.documents_filled ??
          displayState.fields.filter((f) => f.required && f.applies && f.kind === 'file' && f.done)
            .length}{' '}
        /{' '}
        {displayState.documents_required ??
          displayState.fields.filter((f) => f.required && f.applies && f.kind === 'file').length}
      </p>
      <div hidden={mode !== 'instruction'}>
        {state.instruction && (
          <>
            <p>{state.instruction.intro[locale]}</p>
            {state.instruction.notes.map((note, i) => (
              <p key={i}>{note[locale]}</p>
            ))}
            <ol>
              {state.instruction.steps.map((step, i) => (
                <li key={i}>
                  <strong>{step[1][locale]}</strong>
                  <p>{step[2][locale]}</p>
                </li>
              ))}
            </ol>
          </>
        )}
        {module === 'topic' && (
          <ol>
            {(state.templates || [])
              .filter((r) => r.number)
              .map((r) => (
                <li key={r.id} value={r.number}>
                  <strong>{r.title}</strong>
                  <p>{r.description?.[locale]}</p>
                </li>
              ))}
          </ol>
        )}
        {module === 'defense' && <DefenseGuidance groups={state.guidance || []} resources={[]} />}
        <p>
          Подготовьте документы применимого этапа, заполните свою форму и сохраните изменения.
          Отправка на проверку выполняется отдельным действием; после замечаний исправьте указанные
          поля или документы и отправьте раздел повторно.
        </p>
      </div>
      <div hidden={mode !== 'templates'}>
        {state.instruction?.templates_intro && <p>{state.instruction.templates_intro[locale]}</p>}
        <Resources resources={state.templates || state.resources} />
      </div>
      <div hidden={mode !== 'fill'}>
        {module === 'defense' ? (
          <>
            <p>
              В исходной системе этот раздел содержит инструкции и образцы. Личные сведения о защите
              и дипломе заполняются в разделе «Завершение обучения».
            </p>
            {onNavigate && (
              <button className="dp120-mini" onClick={() => onNavigate('completion')}>
                Завершение обучения
              </button>
            )}
          </>
        ) : (
          <>
            {historical && (
              <p className="dp120-badge warn">
                Просмотр отправленной версии от {historical.snapshot_created_at}. Изменение этой
                версии недоступно.
              </p>
            )}
            {preview && (
              <DoctoralDocumentPreview
                key={`${String(preview.fileId)}:${String(preview.versionId)}:${historical?.snapshot_id || 'current'}`}
                title={String(preview.name || 'Документ')}
                load={() =>
                  workflowApi.preview(
                    String(preview.fileId),
                    String(preview.versionId),
                    studentId,
                    historical?.snapshot_id,
                  )
                }
                download={() =>
                  workflowApi.document(
                    String(preview.fileId),
                    String(preview.versionId),
                    studentId,
                    'attachment',
                    historical?.snapshot_id,
                  )
                }
                onClose={() => setPreview(undefined)}
              />
            )}
            <div>
              {!registry && groups.length > 1 && (
                <nav className="dp120-row-actions">
                  <button className="dp120-mini" onClick={() => setSection('all')}>
                    Все этапы
                  </button>
                  {groups.map((g) => (
                    <button className="dp120-mini" key={g} onClick={() => setSection(g)}>
                      {g}
                    </button>
                  ))}
                </nav>
              )}
              {!registry && (
                <div className="dp120-grid">
                  {displayFields
                    .filter((f) => section === 'all' || group(f) === section)
                    .map((f) =>
                      !f.applies ? (
                        <p key={f.path}>Не применяется: {f.label[locale]}</p>
                      ) : f.kind === 'file' ? (
                        <Card key={f.path} title={f.sub ? `${f.sub}` : 'Документ'} span="span12">
                          {fileField(f)}
                        </Card>
                      ) : (
                        <div className="dp120-field full" key={f.path}>
                          {fieldReview(f)}
                          <label htmlFor={f.path}>
                            {f.label[locale]}
                            {f.required ? ' *' : ''}
                          </label>
                          {f.type === 'lessons' ? (
                            <Lessons
                              value={pathValue(historical?.profile || profile, f.path)}
                              disabled={!!studentId || !!historical}
                              onChange={(v) => edit(f.path, v)}
                            />
                          ) : f.type === 'check' ? (
                            <input
                              id={f.path}
                              type="checkbox"
                              disabled={!!studentId || !!historical}
                              checked={pathValue(historical?.profile || profile, f.path) === true}
                              onChange={(e) => edit(f.path, e.target.checked)}
                            />
                          ) : state.options[f.path] ? (
                            <select
                              id={f.path}
                              disabled={!!studentId || !!historical}
                              value={String(
                                pathValue(historical?.profile || profile, f.path) || '',
                              )}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (f.path === 'practiceFlow.practiceType')
                                  act((s) =>
                                    workflowApi.save(module, s.revision, { [f.path]: value }),
                                  );
                                else edit(f.path, value);
                              }}
                            >
                              <option value="">Выберите…</option>
                              {state.options[f.path].map(([v, label]) => (
                                <option key={v} value={v}>
                                  {label}
                                </option>
                              ))}
                            </select>
                          ) : f.type === 'textarea' ? (
                            <textarea
                              id={f.path}
                              disabled={!!studentId || !!historical}
                              value={String(
                                pathValue(historical?.profile || profile, f.path) ?? '',
                              )}
                              onChange={(e) => edit(f.path, e.target.value)}
                            />
                          ) : (
                            <input
                              id={f.path}
                              disabled={!!studentId || !!historical}
                              type={['date', 'number'].includes(f.type) ? f.type : 'text'}
                              min={f.type === 'number' ? 0 : undefined}
                              value={String(
                                pathValue(historical?.profile || profile, f.path) ?? '',
                              )}
                              onChange={(e) => edit(f.path, e.target.value)}
                            />
                          )}
                        </div>
                      ),
                    )}
                </div>
              )}
              {registry && studentId && (
                <Card title="Проверка полей реестра" span="span12">
                  {displayFields
                    .filter((f) => f.kind !== 'file')
                    .map((f) => (
                      <div key={f.path}>
                        <strong>{f.label[locale]}</strong>
                        {fieldReview(f)}
                      </div>
                    ))}
                </Card>
              )}
              {registry && (
                <>
                  {rows.map((row, i) => (
                    <Card
                      key={String(row.id ?? i)}
                      title={String(row.title || 'Запись')}
                      sub={`${row.year || ''} · ${row.type || ''}`}
                      span="span12"
                    >
                      <p>{String(row.authors || row.organization || '')}</p>
                      {!!row.linkedPublicationId && (
                        <p>Связано с публикацией. Изменения вносятся в разделе «Публикации».</p>
                      )}
                      {displayFields
                        .filter((f) => f.kind === 'file')
                        .map((f) => fileField(f, `${module}.${i}.${f.key}`))}
                      {!studentId && !historical && !row.linkedPublicationId && (
                        <div className="dp120-row-actions">
                          <button
                            className="dp120-mini"
                            onClick={() => changeEntry(clone(row), false)}
                          >
                            Изменить
                          </button>
                          <button
                            className="dp120-mini"
                            disabled={busy || conflict}
                            onClick={() =>
                              act((s) =>
                                workflowApi.record(module, s.revision, { id: row.id }, 'delete'),
                              )
                            }
                          >
                            Удалить запись
                          </button>
                        </div>
                      )}
                    </Card>
                  ))}
                  {!studentId && !historical && (
                    <Card title={entry.id ? 'Редактирование записи' : 'Новая запись'} span="span12">
                      <div className="dp120-form">
                        {displayFields
                          .filter((f) => f.kind !== 'file')
                          .map((f) => (
                            <label className="dp120-field" key={f.key}>
                              <span>
                                {f.label[locale]}
                                {f.required ? ' *' : ''}
                              </span>
                              {state.options[f.path] ? (
                                <select
                                  disabled={busy}
                                  value={String(entry[f.key!] ?? '')}
                                  onChange={(e) =>
                                    changeEntry({
                                      ...entryCurrent.current,
                                      [f.key!]: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">Выберите…</option>
                                  {entry[f.key!] &&
                                  !state.options[f.path].some(([v]) => v === entry[f.key!]) ? (
                                    <option value={String(entry[f.key!])}>
                                      {String(entry[f.key!])}
                                    </option>
                                  ) : null}
                                  {state.options[f.path].map(([v, label]) => (
                                    <option key={v} value={v}>
                                      {label}
                                    </option>
                                  ))}
                                </select>
                              ) : f.input_type === 'textarea' ? (
                                <textarea
                                  disabled={busy}
                                  value={String(entry[f.key!] ?? '')}
                                  onChange={(e) =>
                                    changeEntry({
                                      ...entryCurrent.current,
                                      [f.key!]: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                <input
                                  disabled={busy}
                                  type={
                                    ['date', 'number', 'url'].includes(f.input_type || '')
                                      ? f.input_type
                                      : 'text'
                                  }
                                  value={String(entry[f.key!] ?? '')}
                                  onChange={(e) =>
                                    changeEntry({
                                      ...entryCurrent.current,
                                      [f.key!]: e.target.value,
                                    })
                                  }
                                />
                              )}
                            </label>
                          ))}
                      </div>
                      <button
                        className="dp120-mini mint"
                        disabled={busy || conflict}
                        onClick={() => act()}
                      >
                        Сохранить запись
                      </button>
                      {module === 'publications' && !!entry.doi && (
                        <a
                          className="dp120-mini"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://doi.org/${encodeURIComponent(
                            String(entry.doi)
                              .replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, '')
                              .replace(/^doi:\s*/i, ''),
                          )}`}
                        >
                          Открыть DOI ↗
                        </a>
                      )}
                      {!!entry.id && (
                        <button className="dp120-mini" onClick={() => changeEntry({}, false)}>
                          Отменить редактирование
                        </button>
                      )}
                    </Card>
                  )}
                </>
              )}
              {!studentId && !historical && (
                <div className="dp120-row-actions">
                  <button
                    className="dp120-mini mint"
                    disabled={busy || conflict}
                    onClick={() => act()}
                  >
                    Сохранить
                  </button>
                  <button
                    className="dp120-mini"
                    disabled={
                      busy ||
                      conflict ||
                      pending ||
                      state.filled !== state.required ||
                      !state.required ||
                      ['SUBMITTED', 'ACCEPTED'].includes(state.review_status)
                    }
                    onClick={() => act((s) => workflowApi.submit(module, s.revision))}
                  >
                    Отправить на проверку
                  </button>
                </div>
              )}
              {studentId && !historical && (
                <Card title="Проверка отправленной версии" span="span12">
                  <label className="dp120-field">
                    <span>Комментарий</span>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
                  </label>
                  <label className="dp120-field">
                    <span>К чему относится замечание</span>
                    <select
                      value={commentTarget}
                      onChange={(e) => setCommentTarget(e.target.value)}
                    >
                      <option value="">Раздел целиком</option>
                      {state.fields.map((f) => (
                        <option key={f.path} value={f.path}>
                          {f.label[locale]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="dp120-row-actions">
                    {[
                      ['ACCEPTED', 'Подтвердить'],
                      ['NEEDS_CHANGES', 'Вернуть на исправление'],
                    ].map(([decision, label]) => (
                      <button
                        key={decision}
                        className="dp120-mini"
                        disabled={busy || state.review_status !== 'SUBMITTED'}
                        onClick={() =>
                          act((s) =>
                            workflowApi.decide(
                              module,
                              s.revision,
                              studentId,
                              decision,
                              comment,
                              commentTarget || undefined,
                            ),
                          )
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
      {!!state.submissions?.length && (
        <label className="dp120-field">
          <span>Отправленные версии</span>
          <select
            value={historical?.snapshot_id || ''}
            disabled={busy || pending}
            onChange={(e) => {
              const requestId = ++snapshotRequest.current;
              setPreview(undefined);
              if (!e.target.value) {
                setHistorical(undefined);
                return;
              }
              void workflowApi
                .submission(module, e.target.value, studentId)
                .then((value) => {
                  if (snapshotRequest.current === requestId) setHistorical(value);
                })
                .catch(failed);
            }}
          >
            <option value="">Текущая форма</option>
            {state.submissions.map((s, i) => (
              <option key={s.id} value={s.id}>
                Версия {state.submissions!.length - i} · {s.created_at}
              </option>
            ))}
          </select>
        </label>
      )}
      <details>
        <summary>История решений</summary>
        {state.history.length ? (
          state.history.map((h, i) => (
            <p key={h.id || i}>
              {h.created_at} · {h.actor_name} · {statuses[h.decision] || h.decision} {h.comment}
              {h.content_fingerprint ? ` · ${h.content_fingerprint.slice(0, 12)}` : ''}
              {h.target ? ` · ${h.target}` : ''}
            </p>
          ))
        ) : (
          <p>Решений пока нет.</p>
        )}
      </details>
    </>
  );
}
function Lessons({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (v: JsonRecord[]) => void;
  disabled: boolean;
}) {
  const rows = (Array.isArray(value) ? value : []) as JsonRecord[];
  const fields = [
    ['subject', 'Дисциплина'],
    ['lessonType', 'Тип занятия'],
    ['group', 'Группа'],
    ['dateRoom', 'Дата и аудитория'],
    ['time', 'Время'],
  ];
  return (
    <div>
      {rows.map((row, i) => (
        <div key={i} className="dp120-form">
          {fields.map(([key, label]) => (
            <label className="dp120-field" key={key}>
              <span>{label}</span>
              <input
                disabled={disabled}
                value={String(row[key] || '')}
                onChange={(e) =>
                  onChange(rows.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)))
                }
              />
            </label>
          ))}
          {!disabled && (
            <button className="dp120-mini" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              Убрать занятие
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button className="dp120-mini" onClick={() => onChange([...rows, {}])}>
          Добавить занятие
        </button>
      )}
    </div>
  );
}

function DefenseGuidance({ groups, resources }: { groups: DefenseGroup[]; resources: Resource[] }) {
  const { locale } = useI18n();
  return (
    <>
      <Hero
        kicker="DEFENSE"
        title="Предзащита и защита"
        text="Этапы и образцы из действующего Apps Script. Официальные даты, решения и диплом сохраняются в разделе «Завершение обучения»."
      />
      {groups.map((g) => (
        <Card key={g.id} title={g.title[locale]} sub={g.desc[locale]} span="span12">
          <Resources resources={resources.filter((r) => g.items.includes(r.id))} />
        </Card>
      ))}
    </>
  );
}
